import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../../primitives/button";
import { REPORT_REASON_OPTIONS } from "../../../api/reporting/reporting-service";
import { useDialogFocusRestoration } from "../use-dialog-focus";
import "./report-dialog.css";

export default function ReportDialog({
  isOpen,
  title = "Report",
  subtitle = "",
  targetLabel = "this content",
  onClose,
  onSubmit,
}) {
  const [reasonCode, setReasonCode] = useState(REPORT_REASON_OPTIONS[0]?.code || "OTHER");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const containerRef = useRef(null);
  useDialogFocusRestoration(isOpen, containerRef);

  const reasonOptions = useMemo(() => REPORT_REASON_OPTIONS, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onEscape = (event) => {
      if (event.key === "Escape" && !submitting) {
        onClose?.();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = "unset";
      document.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, onClose, submitting]);

  useEffect(() => {
    if (!isOpen) {
      setReasonCode(REPORT_REASON_OPTIONS[0]?.code || "OTHER");
      setDetails("");
      setSubmitting(false);
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const normalizedReason = String(reasonCode || "").trim();
    if (!normalizedReason) {
      setError("Please select a reason.");
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      await onSubmit?.({
        reasonCode: normalizedReason,
        reasonText: String(details || "").trim(),
      });
      onClose?.();
    } catch (submitError) {
      setError(submitError?.message || "Unable to submit report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reportModal__overlay" onClick={() => (!submitting ? onClose?.() : null)}>
      <div
        className="reportModal__container"
        onClick={(event) => event.stopPropagation()}
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          aria-label="Close report dialog"
          className="reportModal__close"
          onClick={onClose}
          disabled={submitting}
        >
          ✕
        </button>

        <h2 className="reportModal__title">{title}</h2>
        {subtitle ? <p className="reportModal__subtitle">{subtitle}</p> : null}
        <p className="reportModal__target">Reporting: {targetLabel}</p>

        <form onSubmit={handleSubmit}>
          <label className="reportModal__label" htmlFor="report-reason">
            Reason
          </label>
          <select
            id="report-reason"
            className="reportModal__select"
            value={reasonCode}
            onChange={(event) => setReasonCode(event.target.value)}
            disabled={submitting}
          >
            {reasonOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>

          <label className="reportModal__label" htmlFor="report-details">
            Additional details (optional)
          </label>
          <textarea
            id="report-details"
            className="reportModal__textarea"
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            placeholder="Add context for admins reviewing this report."
            maxLength={1000}
            disabled={submitting}
          />
          <div className="reportModal__counter">{String(details || "").length}/1000</div>

          {error ? <div className="reportModal__error">{error}</div> : null}

          <div className="reportModal__actions">
            <Button
              variant="ghost"
              type="button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Report"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
